import { useEffect, useState } from "react";
import { API_ENDPOINTS } from "../api/apiConfig";
import { fetchWithAuth } from "../api/fetchWithAuth";

const PAID_TO_OPTIONS = ["RK_FUEL"];
const employeeId = employee => employee?.id ?? employee?.empId ?? employee?.employeeId?.id ?? employee?.employeeId;
const employeeLabel = employee => {
  const name = employee?.name || employee?.fullName || employee?.employeeName || "";
  const code = employee?.empCode || employee?.empcode || employee?.employeeCode || "";
  return code && name ? `${code} - ${name}` : code || name;
};

function FuelPaymentForm({ selectedSite, initialData, onSave, onClose }) {
  const [employees, setEmployees] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(true);
  const [employeesError, setEmployeesError] = useState("");
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fuelDate: "",
    amount: "",
    paidById: "",
    paidTo: "",
    remarks: "",
    document: null,
  });
  const existingPaidBy = initialData?.paidBy || initialData?.paidByEmployee || null;
  const paidById = employeeId(existingPaidBy);
  const employeeOptions = employees.some(employee => String(employeeId(employee) ?? "") === String(paidById ?? ""))
    ? employees
    : paidById != null
      ? [...employees, existingPaidBy]
      : employees;
  const canUseExistingPaidBy = paidById != null;
  const paidToOptions = form.paidTo && !PAID_TO_OPTIONS.includes(form.paidTo)
    ? [...PAID_TO_OPTIONS, form.paidTo]
    : PAID_TO_OPTIONS;

  useEffect(() => {
    const paidBy = initialData?.paidBy || initialData?.paidByEmployee || {};
    setForm({
      fuelDate: (initialData?.fuelDate || "").slice(0, 10),
      amount: initialData?.amount ?? "",
      paidById: String(employeeId(paidBy) ?? ""),
      paidTo: initialData?.paidTo || "",
      remarks: initialData?.remarks || "",
      document: null,
    });
  }, [initialData]);

  useEffect(() => {
    let active = true;
    const loadEmployees = async () => {
      setEmployeesLoading(true);
      setEmployeesError("");
      try {
        const response = await fetchWithAuth(API_ENDPOINTS.employee.list, { method: "GET" });
        if (!response.ok) {
          throw new Error(`Employee request failed (${response.status})`);
        }
        const result = await response.json();
        const employeeRows = Array.isArray(result)
          ? result
          : [result?.employees, result?.data, result?.content].find(Array.isArray);
        if (!employeeRows) {
          throw new Error("Employee response was not a list");
        }
        if (active) setEmployees(employeeRows);
      } catch (error) {
        if (active) setEmployeesError(error.message || "Unable to load employees");
      } finally {
        if (active) setEmployeesLoading(false);
      }
    };

    loadEmployees();
    return () => {
      active = false;
    };
  }, []);

  const submit = async event => {
    event.preventDefault();
    if (selectedSite?.id == null) {
      setFormError("Select a site before adding a fuel payment");
      return;
    }
    if (!form.document && !initialData?.document) {
      window.alert("Please upload a document before saving the Fuel Payment.");
      return;
    }
    const paidBy = employeeOptions.find(employee => String(employeeId(employee) ?? "") === form.paidById);
    if (!paidBy) {
      setFormError("Select an employee who paid for the fuel");
      return;
    }

    setFormError("");
    setSaving(true);
    try {
      await onSave({
        fuelPayment: {
          ...(initialData?.id != null ? { id: initialData.id } : {}),
          fuelDate: form.fuelDate,
          amount: Number(form.amount),
          paidBy,
          site: selectedSite ? { id: selectedSite.id } : null,
          paidTo: form.paidTo.trim(),
          remarks: form.remarks.trim(),
        },
        document: form.document,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="fuel-payment-site">Site *</label>
          <input
            id="fuel-payment-site"
            readOnly
            value={selectedSite?.siteName || selectedSite?.site_name || selectedSite?.name || "No site selected"}
          />
        </div>
        <div className="field">
          <label htmlFor="fuel-payment-date">Fuel Date *</label>
          <input
            id="fuel-payment-date"
            type="date"
            required
            value={form.fuelDate}
            onChange={event => setForm(current => ({ ...current, fuelDate: event.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="fuel-payment-amount">Amount *</label>
          <input
            id="fuel-payment-amount"
            type="number"
            min="0"
            step="0.01"
            required
            value={form.amount}
            onChange={event => setForm(current => ({ ...current, amount: event.target.value }))}
          />
        </div>
        <div className="field">
          <label htmlFor="fuel-payment-paid-by">Paid By Employee *</label>
          <select
            id="fuel-payment-paid-by"
            required
            value={form.paidById}
            disabled={employeesLoading || Boolean(employeesError && !canUseExistingPaidBy)}
            onChange={event => {
              setFormError("");
              setForm(current => ({ ...current, paidById: event.target.value }));
            }}
          >
            <option value="">
              {employeesLoading ? "Loading employees..." : employeesError ? "Unable to load employees" : "Select employee"}
            </option>
            {employeeOptions.map((employee, index) => {
              const id = employeeId(employee);
              return (
                <option key={id ?? index} value={id ?? ""}>
                  {employeeLabel(employee) || "Selected employee"}
                </option>
              );
            })}
          </select>
          {employeesError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{employeesError}</p>}
          {formError && <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>{formError}</p>}
        </div>
        <div className="field">
          <label htmlFor="fuel-payment-paid-to">Paid To *</label>
          <select
            id="fuel-payment-paid-to"
            required
            value={form.paidTo}
            onChange={event => setForm(current => ({ ...current, paidTo: event.target.value }))}
          >
            <option value="">Select recipient</option>
            {paidToOptions.map(option => <option key={option} value={option}>{option}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label htmlFor="fuel-payment-remarks">Remarks</label>
          <textarea
            id="fuel-payment-remarks"
            maxLength={500}
            rows={3}
            value={form.remarks}
            onChange={event => setForm(current => ({ ...current, remarks: event.target.value }))}
          />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label htmlFor="fuel-payment-document">Document {!initialData?.document && "*"}</label>
          <input
            id="fuel-payment-document"
            type="file"
            onChange={event => setForm(current => ({ ...current, document: event.target.files?.[0] || null }))}
          />
          {initialData?.document && !form.document && (
            <small>A document is already attached. Select a file to replace it.</small>
          )}
        </div>
      </div>
      {selectedSite?.id == null && (
        <p role="alert" style={{ color: "#b91c1c", fontSize: 12 }}>Select a site before adding a fuel payment.</p>
      )}
      <div className="form-actions">
        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
        <button
          type="submit"
          className="btn-primary"
          disabled={saving || employeesLoading || Boolean(employeesError && !canUseExistingPaidBy)}
        >
          {saving ? "Saving..." : initialData ? "Update Fuel Payment" : "Save Fuel Payment"}
        </button>
      </div>
    </form>
  );
}

export default FuelPaymentForm;
